import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class UtilityConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "devices" })
  private _devices?: MonitoringProductItem[];

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    devices?: MonitoringProductItem[],
  ) {
    super(id, name, project);
    this.familyNumber = familyNumber;
    this.category = category;
    this._devices = devices;
  }

  static create(
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    devices?: MonitoringProductItem[],
  ): UtilityConfiguration {
    return new UtilityConfiguration(uuidv4(), name, project, familyNumber, category, devices);
  }

  update(familyName: string, familyNumber: string, devices?: MonitoringProductItem[]) {
    this.name = familyName;
    this.familyNumber = familyNumber;
    this.devices = devices;
    this.project.publishUpdate(ProjectEventType.UTILITY_CONFIGURATION_UPDATED, this);
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get devices(): MonitoringProductItem[] | undefined {
    return this._devices;
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set devices(value: MonitoringProductItem[] | undefined) {
    this._devices = value;
  }

  get configuredProductIds(): string[] {
    return [...this.deviceOptionIds];
  }

  getOptions(): MonitoringProductItem[] {
    return [...(this.devices ? this.devices : [])];
  }

  getAllItems(): MonitoringProductItem[] {
    return this.getOptions();
  }

  get deviceOptionIds(): string[] {
    return this.devices?.flatMap((device) => device.option.devices) ?? [];
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedUtilities(this) : 0;
  }
}
